import { useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import TitleCard from "@/components/feature/TitleCard";
import FilterRow from "./FilterRow";
import {
  allRegions,
  allYears,
  filterTitles,
  getCategoryConfig,
  sortOptions,
  sortTitles,
  type SortKey,
} from "@/hooks/useCatalog";

const PAGE_SIZE = 8;

export default function FilterWorkspace() {
  const { type } = useParams();
  const config = getCategoryConfig(type);
  const [searchParams] = useSearchParams();
  const initialGenre = searchParams.get("genre");
  const [genres, setGenres] = useState<string[]>(initialGenre ? [initialGenre] : []);
  const [regions, setRegions] = useState<string[]>([]);
  const [years, setYears] = useState<string[]>([]);
  const [sort, setSort] = useState<SortKey>("heat");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const baseList = useMemo(() => filterTitles({ type: config?.type }), [config?.type]);

  const list = useMemo(() => {
    const result = baseList.filter((item) => {
      const genreMatch = genres.length === 0 || item.genres.some((g) => genres.includes(g));
      const regionMatch = regions.length === 0 || regions.includes(item.region);
      const yearMatch = years.length === 0 || years.includes(String(item.year));
      return genreMatch && regionMatch && yearMatch;
    });
    return sortTitles(result, sort);
  }, [baseList, genres, regions, years, sort]);

  const visible = list.slice(0, visibleCount);

  const toggle = (value: string, current: string[], setter: (next: string[]) => void) => {
    const next = current.includes(value) ? current.filter((item) => item !== value) : [...current, value];
    setter(next);
    setVisibleCount(PAGE_SIZE);
  };

  const reset = () => {
    setGenres([]);
    setRegions([]);
    setYears([]);
    setSort("heat");
    setVisibleCount(PAGE_SIZE);
  };

  const activeCount = genres.length + regions.length + years.length;

  if (!config) return null;

  return (
    <section className="w-full px-4 md:px-8 py-6 md:py-10">
      <div className="mx-auto max-w-[1440px]">
        <div className="rounded-lg border border-background-200 bg-background-50">
          <div className="flex flex-col gap-3 border-b border-background-200 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-sm font-medium text-foreground-900">综合筛选</h2>
              {activeCount === 0 ? (
                <span className="rounded-md bg-background-100 px-2 py-0.5 text-[11px] text-foreground-500">
                  未设置条件
                </span>
              ) : (
                <span className="rounded-md bg-accent-100 px-2 py-0.5 text-[11px] text-accent-800">
                  已选 {activeCount} 个条件
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {activeCount > 0 && (
                <button
                  type="button"
                  onClick={reset}
                  className="inline-flex items-center gap-1 rounded-md border border-background-300 px-3 py-1.5 text-xs text-foreground-600 whitespace-nowrap transition-colors cursor-pointer hover:border-primary-300 hover:text-primary-700"
                >
                  <i className="ri-refresh-line" aria-hidden="true"></i>
                  重置
                </button>
              )}
              <button
                type="button"
                onClick={() => setFiltersOpen((prev) => !prev)}
                className="inline-flex items-center gap-1 rounded-md bg-background-100 px-3 py-1.5 text-xs text-foreground-700 whitespace-nowrap transition-colors cursor-pointer sm:hidden"
              >
                {filtersOpen ? "收起筛选" : "展开筛选"}
                <i className={filtersOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} aria-hidden="true"></i>
              </button>
            </div>
          </div>

          <div className={`${filtersOpen ? "block" : "hidden"} px-4 sm:block sm:px-5`}>
            <FilterRow
              label="类型"
              options={config.genres.filter((g) => g !== "全部")}
              selected={genres}
              onToggle={(value) => toggle(value, genres, setGenres)}
              onSelectAll={() => {
                setGenres([]);
                setVisibleCount(PAGE_SIZE);
              }}
            />
            <FilterRow
              label="地区"
              options={allRegions.filter((r) => r !== "全部")}
              selected={regions}
              onToggle={(value) => toggle(value, regions, setRegions)}
              onSelectAll={() => {
                setRegions([]);
                setVisibleCount(PAGE_SIZE);
              }}
            />
            <FilterRow
              label="年份"
              options={allYears.filter((y) => y !== "全部")}
              selected={years}
              onToggle={(value) => toggle(value, years, setYears)}
              onSelectAll={() => {
                setYears([]);
                setVisibleCount(PAGE_SIZE);
              }}
            />
          </div>
        </div>

        {activeCount > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-xs text-foreground-500">当前筛选：</span>
            {[...genres, ...regions, ...years].map((tag) => (
              <span key={tag} className="rounded-md border border-primary-200 bg-primary-100 px-2 py-0.5 text-[11px] text-primary-800">
                {tag}
              </span>
            ))}
          </div>
        )}

        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-foreground-600">
            共找到 <strong className="font-medium text-primary-700">{list.length}</strong> 部{config.name}作品（演示数据）
          </p>
          <div className="flex items-center gap-1 self-start rounded-full bg-background-100 px-1 py-1 sm:self-auto">
            {sortOptions.map((option) => (
              <button
                key={option.key}
                type="button"
                onClick={() => {
                  setSort(option.key);
                  setVisibleCount(PAGE_SIZE);
                }}
                className={`rounded-full px-3.5 py-1.5 text-xs whitespace-nowrap transition-colors cursor-pointer ${
                  sort === option.key
                    ? "bg-primary-800 text-background-50 font-medium"
                    : "text-foreground-600 hover:text-foreground-900"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {list.length === 0 ? (
          <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-100 text-foreground-500">
              <i className="ri-filter-off-line text-xl" aria-hidden="true"></i>
            </span>
            <h3 className="mt-4 text-sm font-medium text-foreground-900">暂无符合条件的影视作品</h3>
            <p className="mt-2 max-w-sm text-xs leading-relaxed text-foreground-500">
              当前组合条件过窄，试着减少筛选项，或直接重置查看全部作品
            </p>
            <button
              type="button"
              onClick={reset}
              className="mt-5 inline-flex items-center gap-1.5 rounded-md bg-primary-800 px-5 py-2 text-sm text-background-50 whitespace-nowrap transition-colors cursor-pointer hover:bg-primary-700"
            >
              <i className="ri-refresh-line" aria-hidden="true"></i>
              重置筛选条件
            </button>
          </div>
        ) : (
          <>
            <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-5 lg:grid-cols-5">
              {visible.map((item) => (
                <TitleCard key={item.id} item={item} />
              ))}
            </div>
            <div className="mt-8 flex flex-col items-center gap-3">
              {visible.length < list.length ? (
                <button
                  type="button"
                  onClick={() => setVisibleCount((prev) => prev + PAGE_SIZE)}
                  className="inline-flex items-center gap-2 rounded-md bg-secondary-500 px-6 py-2.5 text-sm font-medium text-primary-950 whitespace-nowrap transition-colors cursor-pointer hover:bg-secondary-400"
                >
                  <i className="ri-download-2-line" aria-hidden="true"></i>
                  加载更多
                </button>
              ) : (
                <p className="text-xs text-foreground-500">已经看完全部结果</p>
              )}
              <p className="text-[11px] text-foreground-400">
                已展示 {visible.length} / {list.length} 部作品
              </p>
            </div>
          </>
        )}
      </div>
    </section>
  );
}